import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';
import toast from 'react-hot-toast';
import { MapPin, Clock, Camera, X } from 'lucide-react';

export default function Attendance() {
  const { user, isAdmin, isManager } = useAuth();
  const [myAtt, setMyAtt] = useState([]);
  const [teamAtt, setTeamAtt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingIn, setCheckingIn] = useState(false);
  const [morningStatus, setMorningStatus] = useState('at_site');
  const [showCamera, setShowCamera] = useState(false);
  const [cameraMode, setCameraMode] = useState(null); // 'checkin' or 'checkout'
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const getMonthRange = () => {
    const now = new Date();
    const from = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    const to = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate()}`;
    return { from, to };
  };

  const fetchData = async () => {
    try {
      const range = getMonthRange();
      const myRes = await api.get(`/attendance/my?from=${range.from}&to=${range.to}`);
      setMyAtt(myRes.data.attendance);
      if (isAdmin || isManager) {
        const teamRes = await api.get('/attendance/today');
        setTeamAtt(teamRes.data);
      }
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const todayStr = new Date().toISOString().split('T')[0];
  const todayRecords = myAtt?.filter(a => a.date === todayStr) || [];
  const latestBatch = todayRecords[0]; // most recent batch
  const openBatch = todayRecords.find(a => a.check_in_at && !a.check_out_at);
  const batchCount = todayRecords.length;
  const canCheckIn = !openBatch;
  const canCheckOut = !!openBatch;

  // Camera functions
  const openCamera = async (mode) => {
    setCameraMode(mode);
    setCapturedPhoto(null);
    setShowCamera(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: 640, height: 480 } });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
    } catch (err) {
      toast.error('Camera access denied. Photo is mandatory for attendance.');
      setShowCamera(false);
    }
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth; canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
    setCapturedPhoto(dataUrl);
    stopCamera();
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  };

  const closeCamera = () => { stopCamera(); setShowCamera(false); setCapturedPhoto(null); };

  const confirmPhoto = () => {
    if (cameraMode === 'checkin') doCheckIn(capturedPhoto);
    else doCheckOut(capturedPhoto);
    setShowCamera(false); setCapturedPhoto(null);
  };

  const doCheckIn = async (photoData) => {
    // MANDATORY: Photo required
    if (!photoData) { toast.error('Photo is required for check-in'); return; }

    setCheckingIn(true);
    let lat = null, lng = null;
    try {
      const pos = await new Promise((resolve, reject) => navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }));
      lat = pos.coords.latitude; lng = pos.coords.longitude;
    } catch (e) {
      toast.error('GPS location is required. Please enable location services and try again.');
      setCheckingIn(false);
      return;
    }

    try {
      // Upload photo first
      let photo_s3_key = null;
      const blob = await (await fetch(photoData)).blob();
      const formData = new FormData();
      formData.append('file', blob, `checkin_${Date.now()}.jpg`);
      formData.append('entity_type', 'attendance_checkin');
      formData.append('entity_id', user.id);
      try {
        const uploadRes = await api.post('/media/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        photo_s3_key = uploadRes.data.attachment?.s3_key;
      } catch { toast.error('Photo upload failed'); setCheckingIn(false); return; }

      await api.post('/attendance/check-in', { lat, lng, morning_status: morningStatus, photo_s3_key });
      toast.success('Checked in!');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.error || 'Check-in failed'); }
    setCheckingIn(false);
  };

  const doCheckOut = async (photoData) => {
    if (!photoData) { toast.error('Photo is required for check-out'); return; }

    setCheckingIn(true);
    let lat = null, lng = null;
    try {
      const pos = await new Promise((resolve, reject) => navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }));
      lat = pos.coords.latitude; lng = pos.coords.longitude;
    } catch {
      toast.error('GPS location is required. Please enable location services.');
      setCheckingIn(false);
      return;
    }

    try {
      let photo_s3_key = null;
      const blob = await (await fetch(photoData)).blob();
      const formData = new FormData();
      formData.append('file', blob, `checkout_${Date.now()}.jpg`);
      formData.append('entity_type', 'attendance_checkout');
      formData.append('entity_id', user.id);
      try {
        const uploadRes = await api.post('/media/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        photo_s3_key = uploadRes.data.attachment?.s3_key;
      } catch { toast.error('Photo upload failed'); setCheckingIn(false); return; }

      await api.post('/attendance/check-out', { lat, lng, photo_s3_key });
      toast.success('Checked out!');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.error || 'Check-out failed'); }
    setCheckingIn(false);
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-4 border-brand-600 border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Attendance</h1>

      {/* Camera Modal */}
      {showCamera && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <p className="font-semibold">{cameraMode === 'checkin' ? 'Check-In' : 'Check-Out'} Photo</p>
              <button onClick={closeCamera}><X size={20} /></button>
            </div>
            <div className="p-4">
              {!capturedPhoto ? (
                <>
                  <video ref={videoRef} autoPlay playsInline muted className="w-full rounded-lg bg-gray-900" />
                  <button onClick={capturePhoto} className="w-full mt-3 py-3 bg-brand-600 text-white rounded-xl font-medium flex items-center justify-center gap-2">
                    <Camera size={20} /> Capture Photo
                  </button>
                </>
              ) : (
                <>
                  <img src={capturedPhoto} alt="Captured" className="w-full rounded-lg" />
                  <div className="flex gap-3 mt-3">
                    <button onClick={() => { setCapturedPhoto(null); openCamera(cameraMode); }} className="flex-1 py-2 border rounded-lg text-sm">Retake</button>
                    <button onClick={confirmPhoto} className="flex-1 py-2 bg-green-600 text-white rounded-lg text-sm font-medium">
                      {checkingIn ? 'Processing...' : 'Confirm & Submit'}
                    </button>
                  </div>
                </>
              )}
            </div>
            <div className="px-4 pb-4">
              <button onClick={() => { closeCamera(); if (cameraMode === 'checkin') doCheckIn(null); else doCheckOut(null); }}
                className="w-full text-center text-xs text-gray-400 hover:text-gray-600">Skip photo →</button>
            </div>
          </div>
        </div>
      )}

      {/* Check-in/out Card */}
      <div className="bg-white rounded-xl border p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-gray-800">Today</h2>
          {todayRecords.length > 0 && <span className="text-xs text-gray-400">{todayRecords.length} check-in(s)</span>}
        </div>

        {/* Show all today's batches */}
        {todayRecords.length > 0 && (
          <div className="space-y-2 mb-4">
            {todayRecords.map((rec, i) => {
              const hours = rec.check_in_at && rec.check_out_at ? ((new Date(rec.check_out_at) - new Date(rec.check_in_at)) / 3600000).toFixed(1) : '-';
              return (
                <div key={rec.id} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-bold text-gray-400">#{i + 1}</span>
                    <div>
                      <p className="text-sm">
                        <span className="text-green-600">In: {rec.check_in_at ? new Date(rec.check_in_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '-'}</span>
                        {rec.check_out_at ? <span className="text-red-500 ml-3">Out: {new Date(rec.check_out_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span> : <span className="text-amber-500 ml-3">Still in</span>}
                      </p>
                      {i === 0 && rec.morning_status && <p className="text-[10px] text-gray-400 capitalize">{rec.morning_status.replace('_', ' ')}</p>}
                    </div>
                  </div>
                  <span className="text-xs text-gray-500">{hours !== '-' ? `${hours}h` : ''}</span>
                </div>
              );
            })}
          </div>
        )}

        {/* Check-in button */}
        {canCheckIn && (
          <div className="space-y-3">
            {batchCount === 0 && (
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Morning Status</label>
                <select value={morningStatus} onChange={e => setMorningStatus(e.target.value)} className="px-3 py-2 border rounded-lg text-sm w-full max-w-xs">
                  <option value="at_site">At Site</option>
                  <option value="traveling">Traveling</option>
                  <option value="on_leave">On Leave</option>
                </select>
              </div>
            )}
            <button onClick={() => openCamera('checkin')} disabled={checkingIn}
              className="w-full flex items-center justify-center gap-3 px-8 py-5 heavy-wash active:opacity-90 text-white rounded-2xl font-semibold text-lg disabled:opacity-50 shadow-xl">
              <Camera size={28} /> {checkingIn ? 'Processing...' : batchCount === 0 ? 'Check In with Photo' : 'Check In Again'}
            </button>
          </div>
        )}

        {/* Check-out button */}
        {canCheckOut && (
          <div className="space-y-3">
            <button onClick={() => openCamera('checkout')} disabled={checkingIn}
              className="w-full flex items-center justify-center gap-3 px-8 py-5 bg-brand-900 active:opacity-90 text-white rounded-2xl font-semibold text-lg disabled:opacity-50 shadow-xl">
              <Camera size={28} /> {checkingIn ? 'Processing...' : 'Check Out with Photo'}
            </button>
          </div>
        )}

        {/* All done for now */}
        {!canCheckIn && !canCheckOut && (
          <p className="text-sm text-green-600 font-medium">✓ All checked out for today. Check in again anytime.</p>
        )}
      </div>

      {/* Team Attendance */}
      {teamAtt && (
        <div className="bg-white rounded-xl border">
          <div className="px-5 py-3 border-b"><h2 className="font-semibold text-gray-800">Team Today</h2></div>
          <div className="p-4 space-y-2">
            {teamAtt.attendance?.map(a => (
              <div key={a.id} className="flex items-center justify-between py-2 border-b last:border-0">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-xs font-bold">{a.user?.name?.[0]}</div>
                  <div>
                    <p className="text-sm font-medium">{a.user?.name} <span className="text-gray-400 font-normal">({a.user?.role})</span></p>
                    <p className="text-[10px] text-gray-400">{a.morning_status?.replace('_', ' ')} {a.check_in_lat ? `· GPS: ${Number(a.check_in_lat).toFixed(4)}, ${Number(a.check_in_lng).toFixed(4)}` : ''}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs">{a.check_in_at ? new Date(a.check_in_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '-'}</p>
                  <p className="text-xs text-gray-400">{a.check_out_at ? new Date(a.check_out_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : 'Still in'}</p>
                </div>
              </div>
            ))}
            {teamAtt.notCheckedIn?.length > 0 && (
              <div className="pt-3 border-t">
                <p className="text-xs font-medium text-red-500 mb-2">Not Checked In ({teamAtt.notCheckedIn.length})</p>
                {teamAtt.notCheckedIn.map(u => <p key={u.id} className="text-xs text-gray-500 py-0.5">{u.name} ({u.role})</p>)}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Monthly History */}
      <div className="bg-white rounded-xl border overflow-x-auto">
        <div className="px-5 py-3 border-b"><h2 className="font-semibold text-gray-800">This Month</h2></div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50"><tr>{['Date', 'In', 'Out', 'Status', 'Hours'].map(h => <th key={h} className="text-left px-4 py-2 text-xs font-medium text-gray-500">{h}</th>)}</tr></thead>
          <tbody>
            {myAtt?.map(a => {
              const hours = a.check_in_at && a.check_out_at ? ((new Date(a.check_out_at) - new Date(a.check_in_at)) / 3600000).toFixed(1) : '-';
              return (
                <tr key={a.id} className="border-t">
                  <td className="px-4 py-2">{a.date}</td>
                  <td className="px-4 py-2">{a.check_in_at ? new Date(a.check_in_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                  <td className="px-4 py-2">{a.check_out_at ? new Date(a.check_out_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                  <td className="px-4 py-2 capitalize">{a.morning_status?.replace('_', ' ') || '-'}</td>
                  <td className="px-4 py-2">{hours}h</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
